/**
 * PaymentMethodPicker Component
 * 
 * Dual-rail payment picker for customers with preference='both'.
 * Renders as a chat message when message_type === 'payment_picker'.
 */

import React, { useState } from 'react';
import '../styles/PaymentPicker.css';

export default function PaymentMethodPicker({ message, conversationId }) {
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState('');
  const isPaid = message?.metadata?.payment_completed === true;
  
  if (isPaid) {
    const { payment_confirmed_at, payment_amount, payment_method, payment_tx_hash } = message?.metadata || {};

    const formatDate = (isoStr) => {
      if (!isoStr) return '';
      try {
        return new Date(isoStr).toLocaleDateString('en-US', {
          year: 'numeric', month: 'short', day: 'numeric'
        });
      } catch { return ''; }
    };

    const basescanUrl = payment_tx_hash
      ? `https://basescan.org/tx/${payment_tx_hash}`
      : null;

    return (
      <div className="payment-picker paid-details">
        <div className="paid-header">✅ Paid</div>
        {payment_amount && (
          <div className="paid-amount">{payment_amount}</div>
        )}
        <div className="paid-meta">
          {payment_confirmed_at && (
            <span className="paid-date">{formatDate(payment_confirmed_at)}</span>
          )}
          {payment_method && (
            <span className="paid-method">
              {payment_method === 'usdc' ? 'USDC (Base)' : 'Stripe'}
            </span>
          )}
        </div>
        {basescanUrl && (
          <a href={basescanUrl} target="_blank" rel="noopener noreferrer" className="paid-explorer-link">
            View on BaseScan ↗
          </a>
        )}
      </div>
    );
  }
  
  const { job_summary, amount_cents, options, order_kind, site_deployment_id, order_id } = message?.metadata || {};

  const formatAmount = (cents) => {
    if (!cents && cents !== 0) return '$0.00';
    return `$${(cents / 100).toFixed(2)} USD`;
  };

  const handleSelect = async (method) => {
    if (selecting || !conversationId) return;
    
    setSelecting(true);
    setError('');
    setSelected(method);
    
    try {
      // Link 20 F1: a hosting order's picker pays through /chat/payments/confirm (it has no scope card).
      const isHosting = order_kind === 'hosting';
      const res = isHosting
        ? await fetch('/chat/payments/confirm', {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              payment_type: 'hosting',
              payment_rail: method === 'wallet' ? 'usdc' : 'stripe',
              site_deployment_id,
              order_id,
              conversation_id: Number(conversationId),
              message_id: message?.id,
            }),
          })
        : await fetch(`/chat/conversations/${conversationId}/scope-card/approve`, {
            method: 'POST',
            credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ payment_method_override: method }),
          });
      
      const result = await res.json().catch(() => ({}));
      if (isHosting && res.ok && result?.action === 'error') {
        setError(result.content || result.message || 'Payment was not completed. Please try again.');
        setSelected(null);
        return;
      }
      
      if (!res.ok) {
        throw new Error(result.detail || 'Failed to process selection');
      }
      
      // Log unknown actions for diagnostics
      const knownActions = new Set([
        'add_payment_method', 'job_created', 'stripe_existing',
        'await_usdc_payment', 'payment_confirmed', 'await_payment_method_selection',
        'wallet_required', 'payment_failed', 'denied', 'validation_failed',
        'already_paid', 'already_processing', 'unknown_outcome', 'rail_mismatch',
        'declined', 'order_created', 'usdc_in_flight', 'usdc_unavailable',
        // hosting orders (/chat/payments/confirm)
        'chat_update', 'usdc_payment', 'show_picker', 'error'
      ]);
      if (result?.action && !knownActions.has(result.action)) {
        console.warn('[PaymentMethodPicker] Unknown action from backend:', result.action);
      }

      // On retryable outcomes, return to options
      const retryableActions = new Set(['declined', 'validation_failed', 'payment_failed', 'usdc_in_flight', 'usdc_unavailable']);
      if (result?.action && retryableActions.has(result.action)) {
        setError(result?.error || result?.message?.content || 'Payment was not completed. Please try again.');
        setSelected(null);
        return;
      }
      
      // Success - the response triggers a new message (payment prompt)
      // which will be picked up by chat polling. We just show the selection.
      
    } catch (err) {
      console.error('Payment method selection error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setSelected(null);
    } finally {
      setSelecting(false);
    }
  };

  // If already selected, show confirmation
  if (selected && !error) {
    return (
      <div className="payment-picker selected">
        <span className="selection-confirmed">
          ✓ {selected === 'stripe' ? 'Stripe' : 'USDC'} selected — continuing...
        </span>
      </div>
    );
  }

  const hasStripe = options?.includes('stripe');
  const hasWallet = options?.includes('wallet');

  return (
    <div className="payment-picker">
      <p className="picker-prompt">
        How would you like to pay <strong>{formatAmount(amount_cents)}</strong>
        {job_summary && <> for {job_summary.replace(/[.!?]+\s*$/, '')}</>}?
      </p>
      
      {error && <p className="picker-error">{error}</p>}
      
      <div className="picker-options">
        {hasStripe && (
          <button
            className="picker-btn stripe-btn"
            onClick={() => handleSelect('stripe')}
            disabled={selecting}
          >
            💳 Pay with Stripe (card)
          </button>
        )}
        
        {hasWallet && (
          <button
            className="picker-btn usdc-btn"
            onClick={() => handleSelect('wallet')}
            disabled={selecting}
          >
            ◈ Pay with USDC (Base)
          </button>
        )}
      </div>
      
      {selecting && (
        <p className="picker-loading">Processing...</p>
      )}
    </div>
  );
}
